import { KEY_RECOIL } from '@/shared/config';
import { selectEventChanges, useReportStore } from '@/entities/report';
import { updateStripType } from './helper';
import type { MenuHrChartProps } from './menuHrChart';

/**
 * Drives the HR chart context menu, which retypes the event selected on the chart. Binds the TAB_6 event, control-modal and event-option store actions, exposes whether the selected event is newly created, and on a type button applies updateStripType to the pending event changes and records the result in the undo and redo snapshot.
 *
 * @param props - Carries selectedEvent, the event the chart menu was opened on.
 * @returns An object carrying isSelectNewEvent, setControlModal and onClickButton.
 */
const useMenuHrChart = ({ selectedEvent }: Pick<MenuHrChartProps, 'selectedEvent'>) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending event edits of the HR Summary tab. Forwards the value or updater to the store action along with this tab's key.
   *
   * @param v - Next event-change list or an updater producing it.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  /**
   * Reads the pending event edits of the HR Summary tab. Applies the event-changes selector for this tab key to a one-shot report-store snapshot.
   *
   * @returns The list of edited events.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the modal control state of the HR Summary tab from the chart menu. Forwards the value or updater to the store action along with this tab's key.
   *
   * @param v - Next control modal value or an updater producing it.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the event options snapshot of the HR Summary tab used by undo and redo. Forwards the value or updater to the store action along with this tab's key.
   *
   * @param v - Next event options value or an updater producing it.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const isSelectNewEvent = selectedEvent?.isNew;

  /**
   * Retypes the event selected on the HR chart. Runs updateStripType over the pending event changes for that event, mirrors the result into the event options snapshot for undo and redo, then writes it back as the event changes; a selection without an idEvent is ignored.
   *
   * @param type - Event type to apply to the selected event.
   * @returns Nothing.
   */
  const onClickButton = (type: string) => {
    if (selectedEvent?.idEvent) {
      const stripChangesUpdate = updateStripType({
        stripChanges: getEventChanges(),
        strip: selectedEvent,
        type,
      });
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: stripChangesUpdate,
      }));
      setEventChanges(stripChangesUpdate);
    }
  };

  return {
    isSelectNewEvent,
    setControlModal,
    onClickButton,
  };
};

export default useMenuHrChart;
